import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { Agent } from '../types/models/Agent';
import type { ApiResponseAgent } from '../types/api/ApiResponseAgent';

interface UseAgentFeatureReturn {
  agent: Agent | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * GET /organization/agent — the tenant's agent-feature state (whether the dedicated Keycloak
 * client exists, plus its client id and service-account id). Tenant scoping — including staff
 * impersonation — is handled transparently by `useApiFetch`. Failures are JSend `error` at
 * HTTP 200, so the authoritative success check is `body.status === 'success'` + the `agent`
 * data key, not `response.ok`.
 */
export const useAgentFeature = (): UseAgentFeatureReturn => {
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchAgent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/organization/agent');
      const body = (await response.json().catch(() => null)) as ApiResponseAgent | null;

      if (!response.ok || body?.status !== 'success' || !body.data?.agent) {
        setAgent(null);
        setError(body?.message ?? 'Failed to load the agent feature status');
        return;
      }

      setAgent(body.data.agent);
      setError(null);
    } catch (err) {
      setAgent(null);
      setError(err instanceof Error ? err.message : 'An error occurred while loading the agent feature status');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchAgent();
    }
  }, [auth.isAuthenticated, fetchAgent]);

  return { agent, loading, error, refetch: fetchAgent };
};
